import { Injectable } from "@angular/core";
import { environment } from "../../../../src/environments/environment";
import { Appeaz } from "../models/appeaz";
import { AppeazService } from "./appeaz.service";



/**
 * This class is used to check if an appeaz is using the compatible version of eazly Studio and its version of API. 
 * the compatibility of the versiosn is documented in the java class EazlyApiVersionProvider.
 */
type CompatibilityMap = Map<string, { eazlyVersions: string[], ecitizVersions: string[] }>;

const apiVersionCompatibility: CompatibilityMap = new Map([
    ["1.0", { eazlyVersions: ["02.00.00"], ecitizVersions: ["11.03.00"] }],
    ["1.1", { eazlyVersions: ["02.01.00"], ecitizVersions: ["11.04.00"] }],
    ["1.2", { eazlyVersions: ["02.04.00"], ecitizVersions: ["12.04.00"] }],
    ["1.3", { eazlyVersions: ["02.05.00"], ecitizVersions: ["13.01.00", "13.02.00"] }],
    ["1.4", {
        eazlyVersions: ["02.06.00"], ecitizVersions: ["13.03.00"]
    }],
]);
@Injectable()
export class VersionService {

    currentEazlyVersion: string;
    currentApiVersion: string;
    compatibleVersion: string;
    URLCompatibleversion: string;
    appeazService: AppeazService;
    constructor(appeazService: AppeazService) {
        this.appeazService = appeazService;
    }

    /**
        * This method takes a string representing the current api version and a string representing the current eazly Studio version
        * and checks if the currentEazlyVersion is contained in the versions compatible with the api
        * 
        * @param currentApiVersion - The current api version used by the appeaz 
        * 
        * @param currentEazlyVersion - The current eazly version used by the appeaz 
        * 
        * @returns true if the api version and the eazly Studio version are compatible.
        * 
    */
    public isCompatible(currentApiVersion: string, currentEazlyVersion: string): boolean {
        const compatibility = apiVersionCompatibility.get(currentApiVersion);
        if (compatibility) {
            const eazlyVersionPrefix = currentEazlyVersion.substring(0, 5);
            return compatibility.eazlyVersions.some(version => version.substring(0, 5) === eazlyVersionPrefix);
        }
        return false;
    }


    /**
        * This method takes an Appeaz and checks if its version of eazly Studio
        * is comptible with its version of api. 
        * 
        * @param appeaz - The appeaz 
        * 
        * @returns true if the api version and the eazly Studio version are compatible.
        * 
    */
    public checkCompatibility(appeaz: Appeaz): Promise<boolean> {
        this.currentEazlyVersion = environment.eazlyVersion;
        return new Promise<boolean>((resolve) => {
            this.appeazService.getVersionByAppeaz(appeaz.url).toPromise()
                .then(currentApiVersion => {
                    this.currentApiVersion = currentApiVersion;
                    const compatibility = this.isCompatible(this.currentApiVersion, this.currentEazlyVersion);
                    if (!compatibility) {
                        this.compatibleVersion = this.getCompatibleVersion(this.currentApiVersion);
                        this.URLCompatibleversion = this.getUrlOfCompatibleVersion(this.compatibleVersion);
                    }
                    resolve(compatibility);
                });
        });
    }


    /**
     * This compares two versions in the format xx.xx.xx
     * 
     * @param a - The first version  
     * 
     * @param b - The second version  
     * 
     * @returns -1 if the first version is later,
     * 1 if the second version is later, 
     * 0 if equals.
 */
    public compareVersions(a: string, b: string): number {
        const aParts = a.split('.').map(Number);
        const bParts = b.split('.').map(Number);

        for (let i = 0; i < aParts.length; i++) {
            if (aParts[i] > bParts[i]) {
                return -1
            }
            if (aParts[i] < bParts[i]) {
                return 1
            }
        }
        return 0;
    }

    /**
        * This method takes a version of api and looks for the latest version compatible with it 
        * in the apiVersionCompatibility map 
        * 
        * @param version - The api version 
        * 
        * @returns true if the api version and the eazly Studio version are compatible.
        * 
        * Note: The apiVersionCompatibility doesn't have to be sorted
    */
    public getCompatibleVersion(version: string): string {
        const versions = apiVersionCompatibility.get(version).eazlyVersions;
        versions.sort((a, b) => this.compareVersions(a, b));
        return versions[0];
    }

    /**
        * This method takes a version of studio Eazly and returns its URL 
        * 
        * @param version - The version of the Studio
        * 
        * @returns the URL of the Studio of that version
    */
    public getUrlOfCompatibleVersion(version: string): string {
        const versionParts = version.split('.');
        const simplifiedVersionParts = versionParts.map(part => parseInt(part, 10).toString());
        const simplifiedVersion = simplifiedVersionParts.join('.');
        if (simplifiedVersion.endsWith('.0')) {
            return "https://studio.eazly.com/eazly_fr-" + simplifiedVersion.slice(0, -2);
        }
        return simplifiedVersion;
    }


}
